import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton, Drawer } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import fakeData from 'features/IRWorkstation/constants/fakeData';

const ExplainerPanel = ({ open, onClose }) => {
	const { explainerContent: data } = fakeData;

	return (
		<Drawer
			anchor='right'
			open={open}
			onClose={onClose}
			slotProps={{ paper: { sx: { width: 400, borderRadius: 0 } } }}
		>
			{/* header */}
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'flex-start',
					padding: '10px 16px 12px',
					borderBottom: '0.5px solid',
					borderColor: 'border.light',
					backgroundColor: 'status.blue.bg',
				}}
			>
				<Box>
					<Box
						sx={{
							display: 'inline-flex',
							fontSize: '9.5px',
							fontWeight: 700,
							textTransform: 'uppercase',
							letterSpacing: '0.08em',
							color: 'status.blue.text',
							mb: '4px',
						}}
					>
						💡 Page guide
					</Box>
					<Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
						{data.title}
					</Typography>
				</Box>
				<IconButton
					size='small'
					onClick={onClose}
					sx={{
						width: 26,
						height: 26,
						borderRadius: '6px',
						border: '0.5px solid',
						borderColor: 'status.blue.border',
						color: 'status.blue.text',
						mt: '5px',
						'&:hover': { backgroundColor: 'status.blue.hover' },
					}}
				>
					<CloseIcon sx={{ fontSize: 20 }} />
				</IconButton>
			</Box>

			{/* body */}
			<Box
				sx={{
					padding: '12px 16px',
					overflowY: 'auto',
					flex: 1,
				}}
			>
				{/* about */}
				<Section label='About this page'>
					<Typography
						sx={{
							fontSize: '12px',
							fontWeight: 500,
							color: 'text.primary',
							lineHeight: 1.7,
							mb: 4,
						}}
					>
						{data.about}
					</Typography>
				</Section>

				{/* ai items */}
				<Section label='Where AI plays a role'>
					{data.aiItems.map((item, i) => (
						<Fragment key={i}>
							<DotRow text={item} color='primary.main' />
							{i < data.aiItems.length - 1 && (
								<Box
									sx={{
										borderBottom: '0.5px solid',
										borderColor: 'border.light',
										mb: '6px',
									}}
								/>
							)}
						</Fragment>
					))}
				</Section>

				{/* vdr items */}
				<Section label='↔ Raise VDR connections (click to navigate)'>
					{data.vdrItems.map((item, i) => (
						<Fragment key={i}>
							<DotRow text={item} color='vdr.accent' />
							{i < data.vdrItems.length - 1 && (
								<Box
									sx={{
										borderBottom: '0.5px solid',
										borderColor: 'border.light',
										mb: '6px',
									}}
								/>
							)}
						</Fragment>
					))}
				</Section>

				{/* interactions */}
				<Section label='Try these interactions'>
					{data.interactions.map((item, i) => {
						return (
							<Box
								key={i}
								sx={{
									display: 'flex',
									gap: '9px',
									padding: '8px 10px',
									backgroundColor: 'vdr.bg',
									borderRadius: '7px',
									border: '0.5px solid',
									borderColor: 'vdr.border',
									mb: '6px',
								}}
							>
								<Typography
									sx={{
										fontSize: '11px',
										fontWeight: 700,
										color: 'vdr.text',
										minWidth: '16px',
										mt: '1px',
									}}
								>
									{i + 1}
								</Typography>
								<Typography
									sx={{
										fontSize: '12px',
										fontWeight: 500,
										color: 'vdr.text',
										lineHeight: 1.55,
									}}
								>
									{item}
								</Typography>
							</Box>
						);
					})}
				</Section>
			</Box>
		</Drawer>
	);
};

const Section = ({ label, children }) => (
	<Box sx={{ mb: 4 }}>
		<Typography
			sx={{
				fontSize: '9.5px',
				fontWeight: 700,
				letterSpacing: '0.07em',
				textTransform: 'uppercase',
				color: 'text.tertiary',
				pb: '5px',
				mb: '6px',
				borderBottom: '0.5px solid',
				borderColor: 'border.light',
			}}
		>
			{label}
		</Typography>
		{children}
	</Box>
);

Section.propTypes = {
	label: PropTypes.string.isRequired,
	children: PropTypes.node.isRequired,
};

const DotRow = ({ text, color }) => (
	<Box sx={{ display: 'flex', gap: 1, mb: '6px' }}>
		<Box
			sx={{
				width: 6,
				height: 6,
				borderRadius: '50%',
				mt: '5px',
				flexShrink: 0,
				backgroundColor: color,
			}}
		/>
		<Typography
			sx={{
				fontSize: '12px',
				color: 'text.primary',
				lineHeight: 1.55,
				fontWeight: 500,
			}}
		>
			{text}
		</Typography>
	</Box>
);

DotRow.propTypes = {
	text: PropTypes.string.isRequired,
	color: PropTypes.string.isRequired,
};

ExplainerPanel.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default ExplainerPanel;
